import React, { FC, Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { CarOptionProps } from '@components/v2/CarOption/index';
import { useUILoadingOnConfigurator } from '@hooks/useUILoadingOnConfigurator';
import { useGTM } from '@hooks/useGTM';
import {
    BACKEND_DEEPLINK_REDIRECT_URL,
    BRAND,
    isBrandOV,
    isMarketGB,
    LANGUAGE,
    ModalVersion,
    ROLES,
} from 'src/constants/main';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from 'src/context/featureSwitchApp';
import { useJourneyType } from '@hooks/useJourneyType';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import { useOptionPrice } from '@components/CarOptionsInterior/useOptionPrice';
import useTranslations from '@hooks/useTranslations';
import { theme } from 'src/styles/theme';
import ReactImageFallback from 'react-image-fallback';
import { getAssetUrl } from '@utils/assetUrl.utils';
import { useDispatch } from 'react-redux';
import Checkbox from '@components/Checkbox';
import { useConfiguratorDuck } from '@hooks/useConfiguratorDuck';
import Icon, { Icons } from '@components/Icon';
import { AccessibleButton } from '@components/Button/AccessibleButton';
import { Link } from '@components/v2/link';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import { useRenderDescription } from '@hooks/useRenderDescription';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';

export const CAR_OPTION_NAME_CLASS_NAME = 'option-card__name';

export const TRIM_UPSELL_REDIRECT_PREVIOUS_LCDV_KEY = 'trimUpsellRedirectPreviousLCDV';

export const CarOptionTemplate: FC<CarOptionProps> = ({
    className,
    onChange,
    option,
    onClick,
    sendFullOptionAsParameterOnClick,
    isOpen,
    index,
}) => {
    const { t } = useTranslations();
    const { pushUserAction } = useGTM();
    const { carDetails, currentDeal } = useCarDetailsDuck();
    const { uiIsLoading, uiLoadingDisabledClass } = useUILoadingOnConfigurator();
    const { isFinance } = useJourneyType();
    const { formatConfiguratorPrice } = useMoneyFormatter();
    const { getPrice } = useOptionPrice();
    const dispatch = useDispatch();
    const { selectedOptions } = useConfiguratorDuck();
    const { renderDescription } = useRenderDescription();

    const descriptionRef = useRef<HTMLParagraphElement>();

    const [isSelected, setIsSelected] = useState(false);
    const [isDetailLinkVisible, setIsDetailLinkVisible] = useState(true);
    const [isImageSearchVisible, setIsImageSearchVisible] = useState(true);

    useEffect(() => {
        setIsSelected(selectedOptions.includes(option.id));
    }, [selectedOptions]);

    const isBudgetChangeAllowed = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_BUDGET_CHANGE_ALLOWED);
    const hideCashPayment = !isBudgetChangeAllowed && isFinance;
    const showExtendedPrice = !isMarketGB || (isMarketGB && !hideCashPayment);
    const monthlyPrice = option && option?.prices ? getPrice(option).monthlyPrice : 0;
    const isTiresLabellingEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_TIRES_LABELLING_ENABLED);

    const handleDetailClick = () =>
        dispatch(
            UIDuck.openModal({
                data: {
                    category: option.category?.category,
                    image: option.images[0]['url'],
                    description: renderDescription(option?.description),
                    title: option.title,
                    ...(isTiresLabellingEnabled &&
                        option?.tires?.data && {
                            tiresSubtitle: t('technicalFeatures.tiresDetailModal.title'),
                            tires: option.tires.data,
                            tiresDescription: t('technicalFeatures.tiresDetailModal.subtitle'),
                        }),
                },
                modalType: UIDuck.MODAL_TYPES.CAR_OPTION_DETAIL,
                modalVersion: ModalVersion.v2,
            })
        );

    useEffect(() => {
        if (isOpen && option?.description) {
            setIsDetailLinkVisible(
                descriptionRef?.current?.scrollHeight > descriptionRef?.current?.clientHeight + 5 ||
                    option?.description?.includes('#')
            );
        }
    }, [isOpen]);

    const redirectToMTO = () => {
        localStorage.setItem(TRIM_UPSELL_REDIRECT_PREVIOUS_LCDV_KEY, carDetails.lcdv16);

        window.location.href = `${BACKEND_DEEPLINK_REDIRECT_URL}/?brand=${BRAND}&language=${LANGUAGE}&version=${
            option.id
        }&color=${carDetails.exteriorColour.id}&trim=${carDetails.interiorColour.id}&page=summary${
            currentDeal?.extraFields?.customBackUrl ? `&customBackUrl=${currentDeal?.extraFields?.customBackUrl}` : ''
        }${selectedOptions ? `${selectedOptions?.map((opt, index) => `&opt${index + 1}=${opt}`)}` : ''}`;
    };

    return (
        <div
            className={`${className} option-card ${uiLoadingDisabledClass} ${isSelected ? 'isSelected' : ''}`}
            key={option.id}
            id={`TESTING_OPTION_${option.id}`}
            data-testid={`TESTING_OPTION_${index}`}
        >
            <div className="inner">
                <div className={CAR_OPTION_NAME_CLASS_NAME}>
                    <div
                        data-testid={`TESTING_OPTION_NAME`}
                        onClick={() =>
                            option?.trimUpsell
                                ? redirectToMTO()
                                : onClick && onClick(sendFullOptionAsParameterOnClick ? option : option.id)
                        }
                    >
                        <span>
                            <Checkbox
                                className={'checkbox'}
                                onChange={() =>
                                    option?.trimUpsell
                                        ? redirectToMTO()
                                        : onClick && onClick(sendFullOptionAsParameterOnClick ? option : option.id)
                                }
                                checked={isSelected}
                                name={`${option.id}-option-checkbox`}
                                dataTestId="TESTING_CONFIRM_CHECKBOX"
                                withoutClick
                            />
                        </span>
                        <span>{option.title}</span>
                    </div>
                    <div className="option-card__add-priceWrapper">
                        <span className="option-card__add-priceWrapper-price" data-testid="TESTING_OPTIONS_PRICE">
                            {option?.prices &&
                                showExtendedPrice &&
                                formatConfiguratorPrice(
                                    getPrice(option).totalPrice,
                                    '+ ',
                                    true,
                                    t('configurator.colors.includedInPrice'),
                                    null,
                                    monthlyPrice ? t('configurator.optionPrice.suffix') : ''
                                )}
                            {option?.prices && monthlyPrice && monthlyPrice >= 0
                                ? formatConfiguratorPrice(
                                      monthlyPrice,
                                      '+ ',
                                      true,
                                      t('configurator.colors.includedInPrice'),
                                      null,
                                      `/${t('price.monthly')}`
                                  )
                                : ''}
                            {option?.formattedPrice && option.formattedPrice}
                        </span>
                    </div>
                </div>
                <div className="option-card__wrap">
                    <div>
                        <>
                            <AccessibleButton
                                className={`option-card__imageWrapper ${
                                    !option.images[0]?.url || !isImageSearchVisible
                                        ? 'withoutOptionIcon'
                                        : 'withOptionIcon'
                                }`}
                                tabIndex={0}
                                onClick={handleDetailClick}
                            >
                                <span>
                                    {isTiresLabellingEnabled && option?.tires ? (
                                        <Icon
                                            name={Icons.InfoCircle}
                                            color={theme.colors.primary}
                                            height={20}
                                            width={20}
                                            className="info"
                                            role={ROLES.IMG}
                                            altId="svgImgTyresTechChars"
                                            altText={t('option.detail.shortText.info')}
                                        />
                                    ) : (
                                        isImageSearchVisible && (
                                            <Icon
                                                altId="optionIcon"
                                                altText={t('option.detail.shortText.info')}
                                                name={Icons.Search}
                                                className="optionIcon"
                                                height={24}
                                                width={24}
                                            />
                                        )
                                    )}

                                    <ReactImageFallback
                                        className="option-card__image"
                                        alt={option.title}
                                        src={option.images[0]?.url}
                                        fallbackImage=""
                                        onError={() => setIsImageSearchVisible(false)}
                                    />
                                </span>
                            </AccessibleButton>
                            <p className="option-card__detailDescription" ref={descriptionRef}>
                                {renderDescription(option?.description)}
                            </p>
                            {option?.description && isDetailLinkVisible && (
                                <Link
                                    tabIndex={0}
                                    className="link"
                                    isUnderline
                                    txt={t('option.detail.shortText.info')}
                                    onClick={handleDetailClick}
                                >
                                    <AccessibleButton tabIndex={0} onClick={handleDetailClick}>
                                        <p className="option-card__detailLink">{t('option.detail.shortText.info')}</p>
                                    </AccessibleButton>
                                </Link>
                            )}
                        </>
                    </div>
                </div>
            </div>
        </div>
    );
};
